import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EuroIcon from '@mui/icons-material/Euro';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getTrabajadores, postGasto } from '../../services/apis';
import { getCookie } from '../../utils/cookies';
import { optimizarImagenWebp } from '../../utils/optimizarImagen';

const initialForm = {
    nombre: '',
    observacion: '',
    foto: null,
    cantidad: '',
    id_usuario: '',
    id_apartado: '',
    id_concepto_contabilidad: '',
};

const getUsuarioId = (usuario) => usuario.id_usuario ?? usuario.id ?? usuario.value;
const getUsuarioNombre = (usuario) => usuario.nombre_usuario ?? usuario.nombre ?? usuario.Nombre ?? usuario.label ?? '';

export const AnadirGastoDialog = ({
    open,
    onClose,
    onAdd,
    year,
    cargarDatos,
    currentApartado,
    evento,
    usuarios = [],
    usuarioActualId = '',
    selectorUsuarioBloqueado = false,
    conceptos = [],
    apartados = []
}) => {
    const [form, setForm] = useState(initialForm);
    const [usuariosDialog, setUsuariosDialog] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const nombreUsuarioActual = useMemo(() => localStorage.getItem('DesRepresentante') || '', []);
    const usuariosDisponibles = useMemo(() => (
        (usuariosDialog.length ? usuariosDialog : usuarios).filter((usuario) => getUsuarioId(usuario))
    ), [usuarios, usuariosDialog]);
    const apartadosDisponibles = useMemo(() => (
        apartados.length
            ? apartados
            : (currentApartado || evento?.id_apartado ? [{ id_apartado: currentApartado || evento?.id_apartado, apartado: evento?.asunto || 'Apartat seleccionat', conceptos_predefinidos: conceptos }] : [])
    ), [apartados, conceptos, currentApartado, evento]);
    const conceptosApartadoSeleccionado = useMemo(() => (
        apartadosDisponibles.find((apartado) => String(apartado.id_apartado) === String(form.id_apartado))?.conceptos_predefinidos || []
    ), [apartadosDisponibles, form.id_apartado]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const idApartadoInicial = currentApartado || evento?.id_apartado || apartadosDisponibles[0]?.id_apartado || '';
        setForm((prev) => ({
            ...prev,
            id_apartado: idApartadoInicial,
            id_concepto_contabilidad: '',
        }));
    }, [apartadosDisponibles, currentApartado, evento?.id_apartado, open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setUsuariosDialog(Array.isArray(usuarios) ? usuarios : []);
    }, [open, usuarios]);

    useEffect(() => {
        const cargarUsuariosFallback = async () => {
            if (!open || usuariosDisponibles.length > 0) {
                return;
            }

            const response = await getTrabajadores();
            const listaUsuarios = response?.data || response?.trabajadores || response?.usuarios || [];
            setUsuariosDialog(Array.isArray(listaUsuarios) ? listaUsuarios : []);
        };

        cargarUsuariosFallback();
    }, [open, usuariosDisponibles.length]);

    useEffect(() => {
        if (!open || form.id_usuario || usuariosDisponibles.length === 0) {
            return;
        }

        const usuarioActual = usuariosDisponibles.find((usuario) => String(getUsuarioId(usuario)) === String(usuarioActualId))
            || usuariosDisponibles.find((usuario) => (
                getUsuarioNombre(usuario).toLowerCase() === nombreUsuarioActual.toLowerCase()
            ));
        const usuarioPorDefecto = usuarioActual || usuariosDisponibles[0];

        setForm((prev) => ({
            ...prev,
            id_usuario: getUsuarioId(usuarioPorDefecto) || '',
        }));
    }, [form.id_usuario, nombreUsuarioActual, open, usuarioActualId, usuariosDisponibles]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
            ...(name === 'id_apartado' ? { id_concepto_contabilidad: '' } : {})
        }));
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0] || null;

        if (!file) {
            setForm((prev) => ({ ...prev, foto: null }));
            return;
        }

        try {
            const imagenOptimizada = await optimizarImagenWebp(file);
            setForm((prev) => ({ ...prev, foto: imagenOptimizada }));
        } catch (error) {
            console.error('Error en optimitzar la imatge:', error);
            setForm((prev) => ({ ...prev, foto: file }));
        }
    };

    const handleClose = () => {
        if (guardando) {
            return;
        }

        setError('');
        setForm(initialForm);
        onClose();
    };

    const validar = () => {
        if (!form.nombre.trim()) {
            return 'El nom de la despesa és obligatori.';
        }

        if (form.cantidad === '' || Number(form.cantidad) < 0) {
            return 'Introdueix una quantitat vàlida.';
        }

        if (!form.id_usuario) {
            return 'Selecciona qui afig la despesa.';
        }

        if (!form.id_apartado && !evento?.id) {
            return 'No hi ha cap apartat comptable associat.';
        }

        return '';
    };

    const handleSubmit = async () => {
        const validationError = validar();
        if (validationError) {
            setError(validationError);
            return;
        }

        const formData = new FormData();
        formData.append('token', getCookie('PHPSESSID'));
        formData.append('asunto', form.nombre.trim());
        formData.append('descripcion', form.observacion.trim());
        formData.append('cantidad', form.cantidad);
        formData.append('id_usuario', form.id_usuario);
        formData.append('id_apartado', form.id_apartado || '');
        formData.append('id_evento', evento?.id || '');
        formData.append('id_concepto_contabilidad', form.id_concepto_contabilidad || '');
        formData.append('year', year || new Date().getFullYear());

        if (form.foto) {
            formData.append('foto', form.foto);
        }

        setGuardando(true);
        setError('');

        try {
            const response = await postGasto(formData);
            if (response?.success) {
                await cargarDatos?.();
                onAdd?.(response);
                setForm(initialForm);
                onClose();
                return;
            }

            setError(response?.message || 'Error en guardar la despesa.');
        } catch (err) {
            console.error('Error en guardar la despesa:', err);
            setError('Error en guardar la despesa.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle className="expense-dialog-title">
                <Box className="expense-dialog-icon">
                    <ReceiptLongIcon />
                </Box>
                <Box className="expense-dialog-heading">
                    <Typography className="accounting-eyebrow">Despesa</Typography>
                    <Typography variant="h5">
                        {evento?.asunto || 'Afegir despesa'}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers className="expense-dialog-content">
                {error && (
                    <Alert severity="error" className="expense-dialog-alert">
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            required
                            label="Nom de la despesa"
                            name="nombre"
                            value={form.nombre}
                            onChange={handleChange}
                            inputProps={{ maxLength: 80 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Observació"
                            name="observacion"
                            value={form.observacion}
                            onChange={handleChange}
                            inputProps={{ maxLength: 500 }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            required
                            select
                            label="Afegit per"
                            name="id_usuario"
                            value={form.id_usuario}
                            onChange={handleChange}
                            disabled={selectorUsuarioBloqueado}
                        >
                            {usuariosDisponibles.map((usuario) => (
                                <MenuItem key={getUsuarioId(usuario)} value={getUsuarioId(usuario)}>
                                    {getUsuarioNombre(usuario)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            required
                            select
                            label="Concepte comptable"
                            name="id_apartado"
                            value={form.id_apartado || ''}
                            onChange={handleChange}
                        >
                            {apartadosDisponibles.map((apartado) => (
                                <MenuItem key={apartado.id_apartado} value={apartado.id_apartado}>
                                    {apartado.apartado}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            select
                            label="Concepte predefinit"
                            name="id_concepto_contabilidad"
                            value={form.id_concepto_contabilidad || ''}
                            onChange={handleChange}
                            disabled={!form.id_apartado}
                        >
                            <MenuItem value="">No definit</MenuItem>
                            {conceptosApartadoSeleccionado.map((concepto) => (
                                <MenuItem key={concepto.id} value={concepto.id}>
                                    {concepto.nombre}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            label="Preu"
                            name="cantidad"
                            type="number"
                            value={form.cantidad}
                            onChange={handleChange}
                            inputProps={{ min: '0', step: '0.01' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EuroIcon className="expense-input-icon" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Button variant="outlined" component="label" className="expense-upload-button">
                            <AttachFileIcon />
                            {form.foto ? form.foto.name : 'Adjuntar foto'}
                            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                        </Button>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions className="expense-dialog-actions">
                <Button onClick={handleClose} disabled={guardando}>
                    Cancel·lar
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleSubmit} disabled={guardando}>
                    {guardando ? 'Guardant...' : 'Guardar despesa'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
